<script lang="ts">
	import { MetaTags } from 'svelte-meta-tags';
	import * as m from '$lib/paraglide/messages.js';

	const guides = $derived([
		{
			href: '/guides/what-is-json',
			title: m.guide_what_is_json_h1(),
			desc: m.guide_what_is_json_card(),
			level: m.guides_level_intro(),
			covers: [m.guide_what_is_json_c1(), m.guide_what_is_json_c2(), m.guide_what_is_json_c3()]
		},
		{
			href: '/guides/json-syntax-errors',
			title: m.guide_json_syntax_errors_h1(),
			desc: m.guide_json_syntax_errors_card(),
			level: m.guides_level_intro(),
			covers: [
				m.guide_json_syntax_errors_c1(),
				m.guide_json_syntax_errors_c2(),
				m.guide_json_syntax_errors_c3()
			]
		},
		{
			href: '/guides/json-vs-yaml-xml',
			title: m.guide_json_vs_yaml_xml_h1(),
			desc: m.guide_json_vs_yaml_xml_card(),
			level: m.guides_level_working(),
			covers: [
				m.guide_json_vs_yaml_xml_c1(),
				m.guide_json_vs_yaml_xml_c2(),
				m.guide_json_vs_yaml_xml_c3()
			]
		},
		{
			href: '/guides/json-rest-api',
			title: m.guide_json_rest_api_h1(),
			desc: m.guide_json_rest_api_card(),
			level: m.guides_level_working(),
			covers: [m.guide_json_rest_api_c1(), m.guide_json_rest_api_c2(), m.guide_json_rest_api_c3()]
		},
		{
			href: '/guides/json-schema',
			title: m.guide_json_schema_h1(),
			desc: m.guide_json_schema_card(),
			level: m.guides_level_deep(),
			covers: [m.guide_json_schema_c1(), m.guide_json_schema_c2(), m.guide_json_schema_c3()]
		},
		{
			href: '/guides/large-json-files',
			title: m.guide_large_json_files_h1(),
			desc: m.guide_large_json_files_card(),
			level: m.guides_level_deep(),
			covers: [
				m.guide_large_json_files_c1(),
				m.guide_large_json_files_c2(),
				m.guide_large_json_files_c3()
			]
		}
	]);
</script>

<MetaTags title={m.guides_title()} description={m.guides_description()} />

<div class="guides-index">
	<header class="guides-index__header">
		<span class="eyebrow">"guides"</span>
		<h1 class="guides-index__title">{m.guides_h1()}</h1>
		<p class="guides-index__intro">{m.guides_intro()}</p>
		<p class="guides-index__lede">{m.guides_lede1()}</p>
		<p class="guides-index__lede">{m.guides_lede2()}</p>
	</header>

	<section class="guides-index__path">
		<h2 class="guides-index__path-title">{m.guides_path_title()}</h2>
		<p class="guides-index__path-p">{m.guides_path_p1()}</p>
		<p class="guides-index__path-p">{m.guides_path_p2()}</p>
		<p class="guides-index__path-p">{m.guides_path_p3()}</p>
	</section>

	<div class="guides-index__grid">
		{#each guides as guide (guide.href)}
			<a href={guide.href} class="guides-index__card">
				<span class="guides-index__card-level">{m.guides_level_label()}: {guide.level}</span>
				<h2 class="guides-index__card-title">{guide.title}</h2>
				<p class="guides-index__card-desc">{guide.desc}</p>
				<span class="guides-index__card-covers-label">{m.guides_covers_label()}</span>
				<ul class="guides-index__card-covers">
					{#each guide.covers as item (item)}
						<li>{item}</li>
					{/each}
				</ul>
				<span class="guides-index__card-link">{m.guide_card_read_more()}</span>
			</a>
		{/each}
	</div>
</div>

<style>
	@reference "../../app.css";

	.guides-index {
		@apply max-w-4xl mx-auto;
	}
	.guides-index__header {
		@apply mb-8 pb-6;
		border-bottom: 1px solid color-mix(in srgb, var(--md-outline-variant) 45%, transparent);
	}
	.guides-index__title {
		@apply font-headline font-extrabold tracking-tight mt-2 mb-3;
		font-size: clamp(1.9rem, 3.5vw, 2.6rem);
		line-height: 1.1;
		color: var(--md-on-surface);
	}
	.guides-index__intro {
		@apply text-base md:text-lg max-w-2xl m-0;
		color: var(--md-on-surface-variant);
	}
	.guides-index__lede {
		@apply text-sm md:text-base leading-relaxed mt-3 mb-0 max-w-2xl;
		color: var(--md-on-surface-variant);
		white-space: pre-line;
	}

	/* Reading path */
	.guides-index__path {
		@apply mb-8 p-5 rounded-2xl;
		background: var(--md-surface-container-lowest);
		border: 1px solid color-mix(in srgb, var(--md-outline-variant) 45%, transparent);
	}
	.guides-index__path-title {
		@apply font-headline text-lg font-bold m-0 mb-3 tracking-tight;
		color: var(--md-on-surface);
	}
	.guides-index__path-p {
		@apply text-sm leading-relaxed mt-0 mb-3 last:mb-0;
		color: var(--md-on-surface-variant);
		white-space: pre-line;
	}

	.guides-index__grid {
		@apply grid grid-cols-1 md:grid-cols-2 gap-3;
	}
	.guides-index__card {
		@apply flex flex-col p-5 rounded-2xl no-underline transition-all duration-150;
		background: var(--md-surface-container-lowest);
		border: 1px solid color-mix(in srgb, var(--md-outline-variant) 45%, transparent);
	}
	.guides-index__card:hover {
		border-color: color-mix(in srgb, var(--md-primary) 55%, transparent);
		transform: translateY(-2px);
		box-shadow: 0 14px 30px -18px color-mix(in srgb, var(--md-primary) 60%, transparent);
	}
	.guides-index__card-level {
		@apply font-mono text-[0.625rem] font-semibold uppercase tracking-widest;
		color: var(--md-primary);
	}
	.guides-index__card-title {
		@apply font-headline text-lg font-bold m-0 mt-2 mb-2 tracking-tight;
		color: var(--md-on-surface);
	}
	.guides-index__card-desc {
		@apply text-sm m-0 mb-4 leading-relaxed;
		color: var(--md-on-surface-variant);
	}
	.guides-index__card-covers-label {
		@apply font-mono text-[0.625rem] uppercase tracking-widest;
		color: var(--md-outline);
	}
	.guides-index__card-covers {
		@apply flex flex-col gap-1 mt-1.5 mb-4 pl-0 flex-1 list-none text-[0.8125rem] leading-snug;
		color: var(--md-on-surface-variant);
	}
	.guides-index__card-covers li {
		@apply relative pl-3.5;
	}
	.guides-index__card-covers li::before {
		content: '';
		@apply absolute left-0 top-[0.5em] w-1 h-1 rounded-full;
		background: var(--md-tertiary);
	}
	.guides-index__card-link {
		@apply inline-flex items-center gap-1 text-sm font-semibold;
		color: var(--md-primary);
	}
</style>
